/**
 * useVoiceAgent — React hook for real-time voice interaction with The Director
 *
 * Connects to xAI's Voice Agent API via WebSocket, handles audio capture/playback,
 * and routes tool calls (SQL, file reads) through the backend.
 *
 * Architecture:
 *   1. Frontend calls backend /expert-agent/voice-session to get ephemeral token +
 *      system prompt + tool definitions
 *   2. Frontend opens WebSocket directly to wss://api.x.ai/v1/realtime
 *   3. Audio from mic → PCM16 base64 → WebSocket → xAI
 *   4. xAI → PCM16 base64 audio deltas → AudioContext playback
 *   5. Tool calls from xAI → backend /expert-agent/voice-tool-call → result back to xAI
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { API_BASE } from '../../config';

// ── Types ─────────────────────────────────────────────────────────────────

interface PageContext {
  page_name: string;
  page_path: string;
  page_description?: string;
}

interface VoiceSessionConfig {
  token: string;
  instructions: string;
  tools: VoiceTool[];
  voice: string;
}

interface VoiceTool {
  type: string;
  name?: string;
  description?: string;
  parameters?: Record<string, unknown>;
}

export type VoiceStatus =
  | 'idle'
  | 'connecting'
  | 'listening'
  | 'user_speaking'
  | 'processing'
  | 'agent_speaking'
  | 'error';

interface VoiceTranscript {
  role: 'user' | 'assistant';
  text: string;
  final: boolean;
}

interface PendingToolCall {
  name: string;
  callId: string;
  arguments: string;
}

export interface UseVoiceAgentReturn {
  voiceStatus: VoiceStatus;
  isVoiceActive: boolean;
  startVoice: () => Promise<void>;
  stopVoice: () => void;
  transcripts: VoiceTranscript[];
  errorMessage: string | null;
}

// ── Constants ─────────────────────────────────────────────────────────────

const XAI_REALTIME_URL = 'wss://api.x.ai/v1/realtime';
const SAMPLE_RATE = 24000;
const PROCESSOR_BUFFER_SIZE = 4096;

// ── Audio Helpers ─────────────────────────────────────────────────────────

function float32ToPcm16Base64(float32: Float32Array): string {
  const pcm16 = new Int16Array(float32.length);
  for (let i = 0; i < float32.length; i++) {
    const s = Math.max(-1, Math.min(1, float32[i]));
    pcm16[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  const bytes = new Uint8Array(pcm16.buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

function base64Pcm16ToFloat32(base64: string): Float32Array {
  const binaryString = atob(base64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  const pcm16 = new Int16Array(bytes.buffer);
  const float32 = new Float32Array(pcm16.length);
  for (let i = 0; i < pcm16.length; i++) {
    float32[i] = pcm16[i] / 32768.0;
  }
  return float32;
}

// ── Hook ──────────────────────────────────────────────────────────────────

export default function useVoiceAgent(
  pageContext: PageContext,
  onTranscriptComplete: (role: 'user' | 'assistant', text: string) => void,
): UseVoiceAgentReturn {
  const [voiceStatus, setVoiceStatus] = useState<VoiceStatus>('idle');
  const [transcripts, setTranscripts] = useState<VoiceTranscript[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Refs for WebSocket + audio resources (don't trigger re-renders)
  const wsRef = useRef<WebSocket | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const processorRef = useRef<ScriptProcessorNode | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);

  // Audio playback scheduling
  const nextPlayTimeRef = useRef<number>(0);

  // Transcript accumulator for streaming
  const assistantTranscriptRef = useRef<string>('');

  // Track pending tool calls within a response for parallel execution
  const pendingToolCallsRef = useRef<PendingToolCall[]>([]);
  const responseHasToolCallsRef = useRef<boolean>(false);

  // Track if we're active (to avoid state updates after unmount)
  const isActiveRef = useRef<boolean>(false);

  // ── Cleanup ───────────────────────────────────────────────────────────

  const cleanup = useCallback(() => {
    isActiveRef.current = false;

    // Close WebSocket
    if (wsRef.current) {
      try {
        wsRef.current.close();
      } catch { /* ignore */ }
      wsRef.current = null;
    }

    // Stop mic
    if (processorRef.current) {
      try {
        processorRef.current.disconnect();
      } catch { /* ignore */ }
      processorRef.current = null;
    }
    if (sourceRef.current) {
      try {
        sourceRef.current.disconnect();
      } catch { /* ignore */ }
      sourceRef.current = null;
    }
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(t => t.stop());
      mediaStreamRef.current = null;
    }

    // Close audio context
    if (audioContextRef.current) {
      try {
        audioContextRef.current.close();
      } catch { /* ignore */ }
      audioContextRef.current = null;
    }

    nextPlayTimeRef.current = 0;
    assistantTranscriptRef.current = '';
    pendingToolCallsRef.current = [];
    responseHasToolCallsRef.current = false;
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);

  // ── Audio Playback ────────────────────────────────────────────────────

  const playAudioDelta = useCallback((base64Audio: string) => {
    const ctx = audioContextRef.current;
    if (!ctx || ctx.state === 'closed') return;

    const float32 = base64Pcm16ToFloat32(base64Audio);
    if (float32.length === 0) return;

    const buffer = ctx.createBuffer(1, float32.length, SAMPLE_RATE);
    buffer.copyToChannel(float32, 0);

    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(ctx.destination);

    const now = ctx.currentTime;
    if (nextPlayTimeRef.current < now) {
      nextPlayTimeRef.current = now;
    }
    source.start(nextPlayTimeRef.current);
    nextPlayTimeRef.current += buffer.duration;
  }, []);

  // ── Tool Call Execution ───────────────────────────────────────────────

  const executeToolCall = useCallback(async (
    toolName: string,
    callId: string,
    args: string,
  ): Promise<string> => {
    try {
      const resp = await fetch(`${API_BASE}/expert-agent/voice-tool-call`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          tool_name: toolName,
          call_id: callId,
          arguments: args,
        }),
      });
      if (!resp.ok) {
        return JSON.stringify({ error: `Tool call failed: HTTP ${resp.status}` });
      }
      const data = await resp.json();
      return data.result || JSON.stringify({ error: 'No result returned' });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      return JSON.stringify({ error: `Tool call failed: ${msg}` });
    }
  }, []);

  const sendToolResult = useCallback((callId: string, result: string) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;

    // Send function output
    ws.send(JSON.stringify({
      type: 'conversation.item.create',
      item: {
        type: 'function_call_output',
        call_id: callId,
        output: result,
      },
    }));
  }, []);

  const requestResponseContinuation = useCallback(() => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    ws.send(JSON.stringify({ type: 'response.create' }));
  }, []);

  // ── Process pending tool calls (handles parallel calls) ───────────────

  const processPendingToolCalls = useCallback(async () => {
    const calls = [...pendingToolCallsRef.current];
    pendingToolCallsRef.current = [];
    if (calls.length === 0) return;

    if (isActiveRef.current) {
      setVoiceStatus('processing');
    }

    // Execute all tool calls (can be parallel)
    const results = await Promise.all(
      calls.map(tc => executeToolCall(tc.name, tc.callId, tc.arguments))
    );

    // Send all results back
    for (let i = 0; i < calls.length; i++) {
      sendToolResult(calls[i].callId, results[i]);
    }

    // Wait a brief moment for audio playback to finish, then request continuation
    const ctx = audioContextRef.current;
    if (ctx) {
      const remaining = nextPlayTimeRef.current - ctx.currentTime;
      if (remaining > 0) {
        await new Promise(resolve => setTimeout(resolve, remaining * 1000 + 200));
      }
    }

    requestResponseContinuation();
  }, [executeToolCall, sendToolResult, requestResponseContinuation]);

  // ── WebSocket Message Handler ─────────────────────────────────────────

  const handleWsMessage = useCallback((event: MessageEvent) => {
    if (!isActiveRef.current) return;

    let data: Record<string, unknown>;
    try {
      data = JSON.parse(event.data as string);
    } catch {
      return;
    }

    const eventType = data.type as string;

    switch (eventType) {
      case 'session.created':
      case 'session.updated':
        // Session is ready
        if (isActiveRef.current) {
          setVoiceStatus('listening');
        }
        break;

      case 'input_audio_buffer.speech_started':
        if (isActiveRef.current) {
          setVoiceStatus('user_speaking');
        }
        break;

      case 'input_audio_buffer.speech_stopped':
      case 'input_audio_buffer.committed':
        if (isActiveRef.current) {
          setVoiceStatus('processing');
        }
        break;

      case 'response.output_audio.delta':
        if (isActiveRef.current) {
          setVoiceStatus('agent_speaking');
        }
        if (data.delta && typeof data.delta === 'string') {
          playAudioDelta(data.delta);
        }
        break;

      // xAI uses response.text.delta (not response.output_text.delta)
      case 'response.text.delta':
      case 'response.output_audio_transcript.delta':
        if (data.delta && typeof data.delta === 'string') {
          assistantTranscriptRef.current += data.delta;
          if (isActiveRef.current) {
            const currentText = assistantTranscriptRef.current;
            setTranscripts(prev => {
              const last = prev[prev.length - 1];
              if (last && last.role === 'assistant' && !last.final) {
                return [
                  ...prev.slice(0, -1),
                  { role: 'assistant', text: currentText, final: false },
                ];
              }
              return [...prev, { role: 'assistant', text: currentText, final: false }];
            });
          }
        }
        break;

      case 'response.text.done':
      case 'response.output_audio_transcript.done': {
        const transcript = (data.text as string) || assistantTranscriptRef.current;
        if (transcript && isActiveRef.current) {
          setTranscripts(prev => {
            const last = prev[prev.length - 1];
            if (last && last.role === 'assistant' && !last.final) {
              return [
                ...prev.slice(0, -1),
                { role: 'assistant', text: transcript, final: true },
              ];
            }
            return [...prev, { role: 'assistant', text: transcript, final: true }];
          });
          onTranscriptComplete('assistant', transcript);
        }
        assistantTranscriptRef.current = '';
        break;
      }

      case 'conversation.item.input_audio_transcription.completed': {
        const userText = data.transcript as string;
        if (userText && isActiveRef.current) {
          setTranscripts(prev => [
            ...prev,
            { role: 'user', text: userText, final: true },
          ]);
          onTranscriptComplete('user', userText);
        }
        break;
      }

      case 'response.function_call_arguments.done': {
        // Accumulate tool calls (may be parallel)
        responseHasToolCallsRef.current = true;
        pendingToolCallsRef.current.push({
          name: data.name as string,
          callId: data.call_id as string,
          arguments: data.arguments as string,
        });
        break;
      }

      case 'response.done':
        // If there were tool calls in this response, process them now
        if (responseHasToolCallsRef.current) {
          responseHasToolCallsRef.current = false;
          processPendingToolCalls();
        } else {
          if (isActiveRef.current) {
            setVoiceStatus('listening');
          }
        }
        break;

      case 'error': {
        const errMsg = (data.error as Record<string, unknown>)?.message as string
          || 'Voice session error';
        console.error('[VoiceAgent] Error:', errMsg);
        if (isActiveRef.current) {
          setErrorMessage(errMsg);
          setVoiceStatus('error');
        }
        break;
      }

      default:
        // Ignore unhandled events
        break;
    }
  }, [playAudioDelta, processPendingToolCalls, onTranscriptComplete]);

  // ── Start Voice ───────────────────────────────────────────────────────

  const startVoice = useCallback(async () => {
    if (wsRef.current) return; // Already active

    setVoiceStatus('connecting');
    setErrorMessage(null);
    setTranscripts([]);
    isActiveRef.current = true;

    try {
      // 1. Get voice session config from backend
      const sessionResp = await fetch(`${API_BASE}/expert-agent/voice-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ page_context: pageContext }),
      });

      if (!sessionResp.ok) {
        let detail = `HTTP ${sessionResp.status}`;
        try {
          const errBody = await sessionResp.json();
          if (errBody.detail) detail = errBody.detail;
        } catch { /* ignore */ }
        throw new Error(detail);
      }

      const config: VoiceSessionConfig = await sessionResp.json();

      // 2. Request mic access + create AudioContext (in parallel with WebSocket)
      const [stream] = await Promise.all([
        navigator.mediaDevices.getUserMedia({
          audio: {
            sampleRate: SAMPLE_RATE,
            channelCount: 1,
            echoCancellation: true,
            noiseSuppression: true,
          },
        }),
      ]);

      if (!isActiveRef.current) {
        stream.getTracks().forEach(t => t.stop());
        return;
      }

      mediaStreamRef.current = stream;

      const audioContext = new AudioContext({ sampleRate: SAMPLE_RATE });
      audioContextRef.current = audioContext;

      // Buffer audio while WebSocket connects
      const earlyAudioBuffer: Float32Array[] = [];
      const source = audioContext.createMediaStreamSource(stream);
      sourceRef.current = source;

      const processor = audioContext.createScriptProcessor(PROCESSOR_BUFFER_SIZE, 1, 1);
      processorRef.current = processor;

      source.connect(processor);
      processor.connect(audioContext.destination);

      let wsReady = false;

      processor.onaudioprocess = (e: AudioProcessingEvent) => {
        const input = e.inputBuffer.getChannelData(0);
        const copy = new Float32Array(input);

        if (wsReady && wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
          const base64 = float32ToPcm16Base64(copy);
          wsRef.current.send(JSON.stringify({
            type: 'input_audio_buffer.append',
            audio: base64,
          }));
        } else {
          earlyAudioBuffer.push(copy);
        }
      };

      // 3. Open WebSocket to xAI
      const ws = new WebSocket(XAI_REALTIME_URL, [
        `xai-client-secret.${config.token}`,
      ]);

      wsRef.current = ws;

      ws.onopen = () => {
        if (!isActiveRef.current) {
          ws.close();
          return;
        }

        // Send session configuration
        ws.send(JSON.stringify({
          type: 'session.update',
          session: {
            instructions: config.instructions,
            voice: config.voice,
            tools: config.tools,
            turn_detection: {
              type: 'server_vad',
              threshold: 0.5,
              silence_duration_ms: 800,
              prefix_padding_ms: 300,
            },
            input_audio_transcription: {
              model: 'grok-2-latest',
            },
            audio: {
              input: { format: { type: 'audio/pcm', rate: SAMPLE_RATE } },
              output: { format: { type: 'audio/pcm', rate: SAMPLE_RATE } },
            },
          },
        }));

        // Flush buffered audio
        wsReady = true;
        for (const buf of earlyAudioBuffer) {
          const base64 = float32ToPcm16Base64(buf);
          ws.send(JSON.stringify({
            type: 'input_audio_buffer.append',
            audio: base64,
          }));
        }
        earlyAudioBuffer.length = 0;
      };

      ws.onmessage = handleWsMessage;

      ws.onerror = () => {
        if (isActiveRef.current) {
          setErrorMessage('Voice connection error. Please try again.');
          setVoiceStatus('error');
        }
      };

      ws.onclose = () => {
        if (isActiveRef.current) {
          setVoiceStatus('idle');
        }
        wsRef.current = null;
      };

    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to start voice session';
      console.error('[VoiceAgent] Start failed:', msg);
      setErrorMessage(msg);
      setVoiceStatus('error');
      cleanup();
    }
  }, [pageContext, handleWsMessage, cleanup]);

  // ── Stop Voice ────────────────────────────────────────────────────────

  const stopVoice = useCallback(() => {
    cleanup();
    setVoiceStatus('idle');
    setErrorMessage(null);
  }, [cleanup]);

  // Stop voice on page navigation (pageContext change)
  useEffect(() => {
    return () => {
      if (isActiveRef.current) {
        cleanup();
      }
    };
  }, [pageContext.page_path, cleanup]);

  return {
    voiceStatus,
    isVoiceActive: voiceStatus !== 'idle' && voiceStatus !== 'error',
    startVoice,
    stopVoice,
    transcripts,
    errorMessage,
  };
}
